import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MilestoneManager = () => {
  const [selectedMilestone, setSelectedMilestone] = useState(null);

  const milestones = [
    {
      id: 1,
      title: 'Version 2.1.0',
      status: 'completed',
      date: '2024-09-15',
      features: [
        'Système de partenariat',
        'Génération de liens',
        'Dashboard administrateur'
      ],
      progress: 100,
      type: 'major'
    },
    {
      id: 2,
      title: 'Version 2.2.0',
      status: 'in_progress',
      date: '2024-10-30',
      features: [
        'Analytics temps réel',
        'Notifications push',
        'API mobile'
      ],
      progress: 65,
      type: 'major'
    },
    {
      id: 3,
      title: 'Hotfix 2.1.1',
      status: 'planned',
      date: '2024-10-05',
      features: [
        'Correctifs de sécurité',
        'Optimisations DB'
      ],
      progress: 0,
      type: 'patch'
    },
    {
      id: 4,
      title: 'Version 2.3.0',
      status: 'planned',
      date: '2024-11-30',
      features: [
        'Interface mobile',
        'Système de reviews',
        'Intégrations avancées'
      ],
      progress: 10,
      type: 'major'
    }
  ];

  const upcomingDeadlines = [
    {
      title: 'Code Freeze v2.2.0',
      date: '2024-10-25',
      type: 'deadline',
      urgent: true
    },
    {
      title: 'Security Audit',
      date: '2024-10-20',
      type: 'review',
      urgent: false
    },
    {
      title: 'Performance Testing',
      date: '2024-10-15',
      type: 'testing',
      urgent: false
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success bg-success/10';
      case 'in_progress': return 'text-warning bg-warning/10';
      case 'planned': return 'text-accent bg-accent/10';
      default: return 'text-muted-foreground bg-muted/10';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'major': return 'text-error bg-error/10';
      case 'minor': return 'text-warning bg-warning/10';
      case 'patch': return 'text-success bg-success/10';
      default: return 'text-muted-foreground bg-muted/10';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDaysUntil = (dateString) => {
    const targetDate = new Date(dateString);
    const today = new Date();
    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="space-y-6">
      {/* Milestones */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-card-foreground">Jalons</h3>
          <Button variant="ghost" size="sm">
            <Icon name="Plus" size={16} />
          </Button>
        </div>

        <div className="space-y-4">
          {milestones?.map((milestone) => (
            <div
              key={milestone?.id}
              className={`p-4 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md ${
                selectedMilestone === milestone?.id 
                  ? 'border-accent bg-accent/5' :'border-border bg-background hover:bg-muted/50'
              }`}
              onClick={() => setSelectedMilestone(
                selectedMilestone === milestone?.id ? null : milestone?.id
              )}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getTypeColor(milestone?.type)}`}>
                    {milestone?.type?.toUpperCase()}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(milestone?.status)}`}>
                    {milestone?.status === 'in_progress' ? 'EN COURS' : 
                     milestone?.status === 'completed' ? 'TERMINÉ' : 'PLANIFIÉ'}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-foreground">
                    {milestone?.progress}%
                  </div>
                </div>
              </div>

              <h4 className="font-medium text-foreground mb-1">
                {milestone?.title}
              </h4>

              <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-3">
                <div className="flex items-center space-x-1">
                  <Icon name="Calendar" size={14} />
                  <span>{formatDate(milestone?.date)}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Layers" size={14} />
                  <span>{milestone?.features?.length} features</span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-3">
                <div className="w-full bg-muted rounded-full h-1.5">
                  <div 
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      milestone?.status === 'completed' ? 'bg-success' :
                      milestone?.status === 'in_progress' ? 'bg-warning' : 'bg-accent'
                    }`}
                    style={{ width: `${milestone?.progress}%` }}
                  ></div>
                </div>
              </div>

              {/* Expanded Details */}
              {selectedMilestone === milestone?.id && (
                <div className="pt-3 border-t border-border">
                  <h5 className="text-sm font-medium text-foreground mb-2">Fonctionnalités:</h5>
                  <ul className="space-y-1">
                    {milestone?.features?.map((feature, index) => (
                      <li key={index} className="text-sm text-muted-foreground flex items-center space-x-2">
                        <Icon name="CheckCircle" size={12} className="text-success flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Upcoming Deadlines */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-card-foreground">Échéances</h3>
          <Icon name="AlertTriangle" size={16} className="text-warning" />
        </div>

        <div className="space-y-3">
          {upcomingDeadlines?.map((deadline, index) => {
            const daysUntil = getDaysUntil(deadline?.date);
            const isOverdue = daysUntil < 0;
            const isUrgent = daysUntil <= 3 && daysUntil >= 0;

            return (
              <div
                key={index}
                className={`p-3 rounded-lg border ${
                  isOverdue 
                    ? 'border-error bg-error/5 text-error' 
                    : isUrgent 
                    ? 'border-warning bg-warning/5 text-warning' :'border-border bg-background text-foreground'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium text-sm mb-1">{deadline?.title}</h4>
                    <div className="flex items-center space-x-3 text-xs opacity-80">
                      <div className="flex items-center space-x-1">
                        <Icon name="Calendar" size={12} />
                        <span>{formatDate(deadline?.date)}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="Tag" size={12} />
                        <span className="capitalize">{deadline?.type}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className={`text-xs font-medium ${
                      isOverdue ? 'text-error' : isUrgent ? 'text-warning' : 'text-muted-foreground'
                    }`}>
                      {isOverdue 
                        ? `${Math.abs(daysUntil)} j en retard`
                        : daysUntil === 0 
                        ? "Aujourd'hui"
                        : `${daysUntil} jour${daysUntil > 1 ? 's' : ''}`
                      }
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 pt-4 border-t border-border">
          <Button variant="outline" size="sm" className="w-full">
            <Icon name="Calendar" size={14} className="mr-2" />
            Voir Calendrier Complet
          </Button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-card-foreground mb-4">Actions Rapides</h3>
        
        <div className="space-y-2">
          <Button variant="outline" size="sm" className="w-full justify-start">
            <Icon name="Plus" size={14} className="mr-2" />
            Nouveau Jalon
          </Button>
          <Button variant="outline" size="sm" className="w-full justify-start">
            <Icon name="GitBranch" size={14} className="mr-2" />
            Créer Release
          </Button>
          <Button variant="outline" size="sm" className="w-full justify-start">
            <Icon name="FileText" size={14} className="mr-2" />
            Notes de Version
          </Button>
          <Button variant="outline" size="sm" className="w-full justify-start">
            <Icon name="Download" size={14} className="mr-2" />
            Export Roadmap
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MilestoneManager;